import { useState, useRef, useEffect } from 'react';
import { Plus, ChevronDown } from 'lucide-react';

export default function CreatableSelect({ options, value, onChange, onCreate, placeholder, label }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter(o =>
    o.nome.toLowerCase().includes(search.toLowerCase())
  );
  const selected = options.find(o => Number(o.id) === Number(value));

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreateLoading(true);
    setCreateError('');
    try {
      const created = await onCreate(newName.trim());
      onChange(created.id);
      setNewName('');
      setIsCreating(false);
      setIsOpen(false);
    } catch (err) {
      setCreateError(err.message?.includes('409') || err.message?.toLowerCase().includes('unique')
        ? 'Já existe um registro com este nome'
        : err.message || 'Erro ao cadastrar');
    } finally {
      setCreateLoading(false);
    }
  }

  return (
    <div ref={ref} className="relative">
      {label && <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-left hover:border-[#00DBFF] focus:outline-none focus:border-[#00DBFF]"
      >
        <span className={selected ? 'text-[#535c69]' : 'text-gray-400'}>
          {selected ? selected.nome : placeholder}
        </span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <div className="p-2">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#00DBFF]"
              autoFocus
            />
          </div>
          {filtered.map(o => (
            <button
              key={o.id}
              type="button"
              onClick={() => { onChange(o.id); setIsOpen(false); setSearch(''); }}
              className={`w-full px-3 py-2 text-sm text-left hover:bg-blue-50 ${
                Number(o.id) === Number(value) ? 'bg-blue-50 text-[#00DBFF]' : 'text-[#535c69]'
              }`}
            >
              {o.nome}
            </button>
          ))}
          {filtered.length === 0 && !isCreating && (
            <p className="px-3 py-2 text-xs text-gray-400">Nenhum resultado</p>
          )}
          <div className="border-t border-gray-100">
            {isCreating ? (
              <div className="p-2 space-y-1">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={e => { setNewName(e.target.value); setCreateError(''); }}
                    placeholder="Nome..."
                    className={`flex-1 px-2 py-1.5 text-sm border rounded focus:outline-none focus:border-[#00DBFF] ${createError ? 'border-red-400' : 'border-gray-200'}`}
                    autoFocus
                    disabled={createLoading}
                    onKeyDown={e => e.key === 'Enter' && handleCreate()}
                  />
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={createLoading}
                    className="px-3 py-1.5 bg-[#00DBFF] text-white text-xs rounded hover:bg-[#00DBFF]/80 disabled:opacity-50"
                  >
                    {createLoading ? '...' : 'Salvar'}
                  </button>
                </div>
                {createError && <p className="text-xs text-red-500">{createError}</p>}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#00DBFF] hover:bg-blue-50"
              >
                <Plus size={14} />
                Cadastrar novo
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
